import React, { useState, useContext, useEffect } from 'react';
import { Box, Drawer, useMediaQuery, useTheme } from '@mui/material';
import { Outlet, useLocation } from 'react-router-dom';
import SidebarContent from '../Navbar/Sidebar1';
import { useOrganizations } from '../../customHooks';
import { Navbar } from '../Navbar';
import { ColorModeContext } from '../../index';  // bring in the context

export const Layout = () => {
  const { sidebarModules } = useOrganizations();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();

  useEffect(() => {
    const rawPath = location.pathname.replace(/^\//, '');
    let pageTitle = '';

    const routeTitleMap = {
      '': 'Dashboard',
      'home': 'Dashboard',
      'dashboard': 'Dashboard',
      'devices': 'Devices',
      'policies': 'Policies',
      'permissions': 'Permissions',
      'roles': 'Roles',
      'role': 'Roles',
      'users': 'Users',
      'user-management': 'User Management',
      'groups': 'Groups',
      'urls': 'Whitelist URLs',
      'protocols': 'Protocol Rules',
      'active-sessions': 'Active Sessions',
      'browser-updates': 'Latest Browser Updates',
      'questionnaire': 'Questionnaire',
      'custom-charts': 'Custom Charts',
      'reports': 'Reports',
      'release-notes': 'Release Notes',
      'profile': 'Profile',
      'asset': 'Asset & Firmware',
      'bulk-import': 'Bulk Import',
      'scheduler': 'Scheduler',
      'scheduler1': 'Scheduler',
      'email-scheduler': 'Email Notification',
      'notifications': 'Notifications',
      '404': 'Page Not Found'
    };

    if (routeTitleMap[rawPath] !== undefined) {
      pageTitle = routeTitleMap[rawPath];
    } else {
      const segment = rawPath.split('/')[0] || '';
      pageTitle = segment
        .split('-')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    }

    if (pageTitle) {
      document.title = `${pageTitle} | mSecure Browser`;
    } else {
      document.title = 'mSecure Browser - Modern Secure Browsing Platform';
    }
  }, [location.pathname]);
  
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  
  const toggleSidebar = () => {
    if (isMobile) {
      setMobileOpen(!mobileOpen);
    } else {
      setIsSidebarCollapsed(prev => !prev);
    }
  };

  // Consume the color mode context
  const { mode, toggleColorMode } = useContext(ColorModeContext);

  const sidebarBg = mode === 'dark' ? '#0b1120' : 'var(--vault-navy)';

  const drawerContent = (
    <SidebarContent
      isCollapsed={isMobile ? false : isSidebarCollapsed}
      toggleSidebar={toggleSidebar}
    />
  );

  return (
    <Box sx={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>
      {sidebarModules && (
        <Box
          component="nav"
          sx={{
            width: isMobile ? 0 : (isSidebarCollapsed ? '72px' : '260px'),
            flexShrink: { md: 0 },
            transition: 'width 0.3s ease',
          }}
        >
          {isMobile ? (
            <Drawer
              variant="temporary"
              open={mobileOpen}
              onClose={toggleSidebar}
              ModalProps={{
                keepMounted: true, // Better open performance on mobile.
              }}
              sx={{
                display: { xs: 'block', md: 'none' },
                '& .MuiDrawer-paper': { 
                  boxSizing: 'border-box', 
                  width: '260px', 
                  background: sidebarBg,
                  display: 'flex',
                  flexDirection: 'column',
                  minHeight: 0,
                  height: '100vh'
                },
              }}
            >
              {drawerContent}
            </Drawer>
          ) : (
            <Drawer
              variant="permanent"
              sx={{
                display: { xs: 'none', md: 'block' },
                '& .MuiDrawer-paper': { 
                  boxSizing: 'border-box', 
                  width: isSidebarCollapsed ? '72px' : '260px', 
                  background: sidebarBg, 
                  overflow: 'hidden', 
                  transition: 'width 0.3s ease',
                  display: 'flex',
                  flexDirection: 'column',
                  minHeight: 0,
                  height: '100vh'
                },
              }}
              open
            >
              {drawerContent}
            </Drawer>
          )}
        </Box>
      )}

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          height: '100%',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          width: isMobile ? '100%' : `calc(100% - ${isSidebarCollapsed ? '72px' : '260px'})`,
          transition: 'width 0.3s ease',
        }}
      >
        <Navbar
          showSideBarOption={!!sidebarModules}
          isCollapsed={isSidebarCollapsed}
          toggleSidebar={toggleSidebar}
          mode={mode}
          toggleMode={toggleColorMode}
          isMobile={isMobile}
        />

        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'auto', bgcolor: 'var(--mist)' }}>
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
};
